import { useSession } from '@side/next-auth/react';
import { useMemo } from 'react';
import { useIdentity } from '$api-clients/hooks';

interface UseSelfTeamMembershipsOptions {
  q?: string;
  skip?: boolean;
}

/**
 * The current user's own team memberships. The endpoint omits the member's own
 * identity fields, so each row is enriched with them from the session.
 */
export function useSelfTeamMemberships({
  q,
  skip,
}: UseSelfTeamMembershipsOptions = {}) {
  const { data: session } = useSession();
  const sessionUser = session?.user;
  const userId = sessionUser?.id;

  const { data, isLoading, error } = useIdentity(
    '/users/{userId}/team-members',
    userId && !skip
      ? {
          params: {
            path: { userId },
            query: q ? { q } : {},
          },
        }
      : null,
    { keepPreviousData: true },
  );

  const memberships = useMemo(() => {
    if (!data?.data || !sessionUser) return undefined;

    return data.data.map((membership) => ({
      ...membership,
      userId: sessionUser.id,
      legacyExternalId: sessionUser.legacyExternalId,
      firstName: sessionUser.firstName,
      lastName: sessionUser.lastName,
      photoUrl: sessionUser.photoUrl,
    }));
  }, [data?.data, sessionUser]);

  return {
    memberships,
    loading: isLoading,
    error,
  };
}
